import { useContext, useRef } from "react";
import { getDataFromStorage } from "../../util";
import useAxiosPrivate from "../useAxiosPrivate";
import audioBufferToWav from "audiobuffer-to-wav";
import AlertContext from "../../context/AlertContext";
import { useReactMediaRecorder } from "react-media-recorder";
import useS3FileManagement from "../useS3FileManagement";
import useCommonFunctionHooks from "../useCommonFunctionHooks";
import { StudentTestPageContext } from "../../context/StudentTestPageContext";
import CodingHeaderLearnerSideHooks from "../CodingPage/CodingHeaderLearnerSideHooks";
import InstructionPageHooks from "../InstructionPage/InstructionPageHooks";

export default function InterviewHooks() {
  const Alert = useContext(AlertContext);
  const StudentTestPageCxt = useContext(StudentTestPageContext);

  const axios = useAxiosPrivate();
  const { onMultiPartUpload } = useS3FileManagement();
  const { submitAnswers } = CodingHeaderLearnerSideHooks();
  const { handleClose } = useCommonFunctionHooks();
  const { removeAccess } = InstructionPageHooks();

  const containerRef = useRef(null);

  let { startRecording, stopRecording, previewStream } = useReactMediaRecorder({
    video: true,
    onStop: (blobUrl, blob) => handleStopRecord(blobUrl, blob),
  });

  const playQns = (text, data) => {
    if (!text.hasOwnProperty("actualscore")) {
      text = text?.name || text;
      Alert.setButtonDisable(true);
      text?.replace(/<[^>]*>/g, "");

      let speech = new SpeechSynthesisUtterance();

      const voices = window.speechSynthesis.getVoices();

      let indianFemaleVoice = voices.find(
        (voice) =>
          voice.lang === "en-IN" && voice.name?.toLowerCase().includes("female")
      );

      if (indianFemaleVoice) {
        speech.voice = indianFemaleVoice;
      } else {
        speech.voice = voices.find(
          (voice) => voice.name === "Google UK English Female"
        );
      }

      speech.text = text;

      speech.onstart = () => {
        StudentTestPageCxt.setIsSpeaking((prev) => ({
          ...prev,
          ...(StudentTestPageCxt.showButton.next ||
          StudentTestPageCxt.showButton.last
            ? { feedback: true }
            : { question: true }),
        }));
      };

      speech.onend = () => {
        if (data?.from === "qns") {
          startRecording();
          data.startAudioRecording();
        }

        Alert.setButtonDisable(false);
        StudentTestPageCxt.setIsSpeaking(StudentTestPageCxt.defaultIsSpeaking);
      };

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(speech);
    } else {
      StudentTestPageCxt.transcript.current = text.transcript;
      StudentTestPageCxt.setShowButton((prev) => ({
        ...prev,
        next: true,
        view: true,
      }));
    }
  };

  const handleNext = () => {
    StudentTestPageCxt.transcript.current = "";
    StudentTestPageCxt.recognitionRef.current = null;
    if (
      StudentTestPageCxt.currentQnIndex <
      Alert.comingObj.sections[0].questions.length - 1
    ) {
      StudentTestPageCxt.setCurrentQnIndex(
        StudentTestPageCxt.currentQnIndex + 1
      );
      StudentTestPageCxt.setShowButton(StudentTestPageCxt.defaultShowButton);
    }
  };

  const handlePrevious = () => {
    StudentTestPageCxt.transcript.current = "";

    if (
      StudentTestPageCxt.currentQnIndex > 0 &&
      StudentTestPageCxt.currentQnIndex <=
        Alert.comingObj.sections[0].questions.length - 1
    ) {
      StudentTestPageCxt.setCurrentQnIndex(
        StudentTestPageCxt.currentQnIndex - 1
      );
      StudentTestPageCxt.setShowButton(StudentTestPageCxt.defaultShowButton);
    }
  };

  const handleBack = (data) => {
    Alert.setButtonDisable(false);

    if (data.from === "back") {
      if (StudentTestPageCxt.showButton.view) {
        if(Alert.isAdmin){
          Alert.navigate(-1);
        }
        else{
          Alert.navigate("/main/interview/results");
        }
      } else {
        Alert.setShowNotify({
          show: true,
          buttonStyle: "fill",
          secondaryButton: "End Interview",
          primaryButton: "Continue Interview",
          title: "Are you sure you want to exit the interview?",
          msg: "Exiting now will end the current interview, and your progress will not be saved.",
          secondaryButtonFunction: () => {
            handleClose();
            removeAccess();
            stopRecording();
            data.stopAudioRecording();

            if (document.fullscreenElement) {
              document.exitFullscreen();
            }

            Alert.navigate("main/interview/library");
          },
        });
      }
    } else {
      submitAnswers(false, false, "interview");
    }
  };

  const handleFeedBack = () => {
    if (StudentTestPageCxt.showButton.next) {
      if (StudentTestPageCxt.showButton.currentQnsAuto) {
        return `You were unable to answer this question within the given time. We will submit your response for this question. You can 
        proceed to the next one.`;
      } else {
        return "Thank you for your response, let's proceed to the next question";
      }
    } else {
      if (
        StudentTestPageCxt.showButton.last &&
        StudentTestPageCxt.showButton.currentQnsAuto
      ) {
        return `You were unable to answer this question within the given time. We will submit your response. You have successfully completed 
        the AI interview. We will notify you of the results after evaluation.`;
      } else if (StudentTestPageCxt.showButton.testAuto) {
        return `You were unable to complete the test within the given time. We will submit your response. You have successfully completed the 
        AI interview. We will notify you of the results after evaluation.`;
      } else {
        return `Thank you for your response! You have successfully completed the AI interview. We will notify you of the results after 
        evaluation.`;
      }
    }
  };

  const compressAudio = (audioBuffer) => {
    const channels = 1;
    const sampleRate = 16000;

    const offlineCtx = new OfflineAudioContext(
      channels,
      audioBuffer.duration * sampleRate,
      sampleRate
    );

    const source = offlineCtx.createBufferSource();

    source.buffer = audioBuffer;
    source.connect(offlineCtx.destination);
    source.start();

    return offlineCtx.startRendering().then((renderedBuffer) => {
      const wavData = audioBufferToWav(renderedBuffer, { bitDepth: 8 });

      return new Blob([wavData], { type: "audio/wav" });
    });
  };

  const stopAudioRecording = async (blob) => {
    const audioArrayBuffer = await blob.arrayBuffer();

    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    const audioBuffer = await audioContext.decodeAudioData(audioArrayBuffer);
    const wavBlob = await compressAudio(audioBuffer);

    const form = new FormData();

    form.append("file", wavBlob);

    axios.post("node/learner/speech/text", form).then((res) => {
      stopRecording();
      Alert.qnTrack.current += 1;
      StudentTestPageCxt.transcript.current = res.data.transcript;

      if (
        StudentTestPageCxt.currentQnIndex <
        Alert.comingObj.sections[0].questions.length - 1
      ) {
        StudentTestPageCxt.setShowButton((prev) => ({
          ...prev,
          ...(StudentTestPageCxt.recognitionRef.current === "currentQnsAuto"
            ? { next: true, currentQnsAuto: true }
            : StudentTestPageCxt.recognitionRef.current === "testAuto"
            ? { last: true, testAuto: true }
            : { next: true }),
        }));
      } else {
        StudentTestPageCxt.setShowButton((prev) => ({
          ...prev,
          ...(StudentTestPageCxt.recognitionRef.current === "currentQnsAuto"
            ? { last: true, currentQnsAuto: true }
            : StudentTestPageCxt.recognitionRef.current === "testAuto"
            ? { last: true, testAuto: true }
            : { last: true }),
        }));
      }
    });

    Alert.setButtonDisable(true);
  };

  const handleStopRecord = async (blobUrl, blob) => {
    let data =
      Alert.comingObj.sections[0].questions[StudentTestPageCxt.currentQnIndex];

    let anstext = `VideoRecord/${Alert.comingObj.lmpid}/${
      Alert.comingObj.testid
    }/${getDataFromStorage("learnerid")}/${data.id}.mp4`;

    onMultiPartUpload(anstext, blob).then((res) => {
      if (res === "Success") {
        Alert.setVideoSaved((prev) => prev + 1);
        Alert.updateComingObj((prev) => ({
          ...prev,
          answer: Array.isArray(prev.answer)
            ? [
                ...prev.answer,
                {
                  qnid: data.id,
                  report: false,
                  answer: anstext,
                  transcript: StudentTestPageCxt.transcript.current,
                },
              ]
            : [
                {
                  qnid: data.id,
                  report: false,
                  answer: anstext,
                  transcript: StudentTestPageCxt.transcript.current,
                },
              ],
        }));
      }
    });
  };

  return {
    playQns,
    handleNext,
    handleBack,
    containerRef,
    previewStream,
    stopRecording,
    handlePrevious,
    handleFeedBack,
    startRecording,
    stopAudioRecording,
  };
}
